import { preselectService } from "@/lib/scroll";
import Icon from "@/components/Icon";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useLang } from "@/i18n";

const ITEMS = ["Smarten Inverter", "Inverter Battery", "Lithium Battery"];

function FlowDiagram({ labels }) {
    const xs = [60, 220, 380, 540];
    return (
        <svg viewBox="0 0 600 150" className="w-full" role="img" aria-label="Electricity flows through inverter and battery to your home">
            {[0, 1, 2].map((i) => (
                <line
                    key={i}
                    x1={xs[i] + 42}
                    y1="60"
                    x2={xs[i + 1] - 42}
                    y2="60"
                    stroke="#00CFFF"
                    strokeWidth="1.6"
                    className="dash-flow"
                />
            ))}
            <g transform="translate(60,60)">
                <circle r="34" fill="#0B1B33" stroke="rgba(0,207,255,0.4)" strokeWidth="1.2" />
                <path d="M2 -16 L-8 2 H-1 L-4 16 L10 -4 H2 Z" fill="#00CFFF" className="animate-energy" />
            </g>
            <g transform="translate(220,60)">
                <circle r="34" fill="#0B1B33" stroke="rgba(0,207,255,0.4)" strokeWidth="1.2" />
                <rect x="-16" y="-12" width="32" height="24" rx="4" fill="none" stroke="#E8F0F7" strokeWidth="1.6" />
                <circle cx="8" cy="0" r="2.6" fill="#00CFFF" className="animate-pulse-dot" />
            </g>
            <g transform="translate(380,60)">
                <circle r="34" fill="#0B1B33" stroke="rgba(0,207,255,0.4)" strokeWidth="1.2" />
                <rect x="-11" y="-15" width="22" height="30" rx="4" fill="none" stroke="#E8F0F7" strokeWidth="1.6" />
                <rect x="-7" y="2" width="14" height="9" rx="2" fill="#00CFFF" className="animate-energy" />
            </g>
            <g transform="translate(540,60)">
                <circle r="34" fill="#0B1B33" stroke="rgba(255,210,31,0.45)" strokeWidth="1.2" />
                <path d="M-13 2 L0 -11 L13 2 M-9 2 V13 H9 V2" fill="none" stroke="#FFD21F" strokeWidth="1.8" />
            </g>
            {xs.map((x, i) => (
                <text key={x} x={x} y="126" textAnchor="middle" fill="rgba(13,27,46,0.55)" fontSize="10" fontFamily="JetBrains Mono, Noto Sans Bengali, monospace" letterSpacing="2">
                    {labels[i]}
                </text>
            ))}
        </svg>
    );
}

export default function PowerBackup() {
    const { t } = useLang();
    return (
        <section id="power-backup" data-testid="power-backup-section" className="relative py-24 md:py-32 lg:py-40 border-y border-ink/5 bg-navy overflow-hidden">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[20rem] bg-electric/10 blur-[130px] rounded-full pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                <Reveal className="text-center flex flex-col items-center">
                    <Eyebrow>{t.power.eyebrow}</Eyebrow>
                    <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-ink mt-4">
                        {t.power.headingA}<span className="text-cyan2">{t.power.headingB}</span>
                    </h2>
                    <p className="mt-5 max-w-xl text-ice/60 text-base md:text-lg">
                        {t.power.sub}
                    </p>
                </Reveal>

                <Reveal delay={0.15} className="mt-14">
                    <div className="rounded-3xl border border-ink/10 bg-surface/80 p-6 md:p-10">
                        <FlowDiagram labels={t.power.flow} />
                    </div>
                </Reveal>

                <div className="mt-10 flex flex-wrap justify-center gap-3">
                    {ITEMS.map((item, i) => (
                        <Reveal key={item} delay={0.2 + i * 0.1}>
                            <button
                                data-testid={`power-item-${item.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => preselectService(item)}
                                className="flex items-center gap-2.5 glass rounded-full px-6 py-3 text-sm font-semibold text-ink/80 hover:border-cyan2/40 hover:text-cyan2 transition-colors duration-300"
                            >
                                <Icon name="battery" size={16} className="text-cyan2" />
                                {item}
                            </button>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
